import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Car,
  Plus,
  FileText,
  TrendingUp
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CONTACT_EMAIL, CONTACT_PHONE } from '../config/legal';

const API = `${process.env.REACT_APP_BACKEND_URL || '/_/backend'}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch user if not provided
        if (!user) {
          const userRes = await fetch(`${API}/auth/me`, { credentials: 'include' });
          if (userRes.ok) {
            const userData = await userRes.json();
            setUser(userData);
          }
        }

        // Fetch applications
        const appsRes = await fetch(`${API}/applications`, { credentials: 'include' });
        if (appsRes.ok) {
          const appsData = await appsRes.json();
          setApplications(appsData);
        }
      } catch (e) {
        console.error('Error fetching data:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-NZ', {
      style: 'currency',
      currency: 'NZD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-NZ', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const config = {
      scored: { icon: Clock, className: 'bg-gray-100 text-gray-600', label: 'Eligibility Assessed' },
      submitted: { icon: Clock, className: 'bg-amber-100 text-amber-700', label: 'Submitted' },
      pending: { icon: Clock, className: 'bg-amber-100 text-amber-700', label: 'Pending Review' },
      reviewing: { icon: AlertCircle, className: 'bg-blue-100 text-blue-700', label: 'Under Review' },
      approved: { icon: CheckCircle, className: 'bg-[#00D47E]/10 text-gray-700', label: 'Approved' },
      conditionally_approved: { icon: CheckCircle, className: 'bg-[#00D47E]/10 text-gray-700', label: 'Conditionally Approved' },
      referred: { icon: AlertCircle, className: 'bg-blue-100 text-blue-700', label: 'Referred' },
      declined: { icon: XCircle, className: 'bg-red-100 text-red-700', label: 'Declined' },
      completed: { icon: CheckCircle, className: 'bg-purple-100 text-purple-700', label: 'Completed' }
    };
    const { icon: Icon, className, label } = config[status] || config.pending;
    
    return (
      <Badge className={`${className} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {label}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Header */}
      <section className="pt-24 pb-8 md:pt-32 md:pb-12 bg-gradient-to-br from-black via-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          >
            <div>
              <h1 className="font-outfit text-3xl md:text-4xl font-bold text-white mb-2">
                Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
              </h1>
              <p className="text-gray-400">
                Track your finance applications
              </p>
            </div>
            <Button
              data-testid="new-application-btn"
              onClick={() => navigate('/calculator')}
              className="bg-[#00D47E] text-black hover:bg-[#00D47E]/80 rounded-full px-6"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Application
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Dashboard Content */}
      <section data-testid="dashboard-content" className="py-12">
        <div className="max-w-7xl mx-auto px-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl border border-gray-200 p-6"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#00D47E]/10 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-[#00D47E]" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Applications</p>
                  <p className="font-outfit text-2xl font-bold text-black">
                    {applications.length}
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl border border-gray-200 p-6"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Pending</p>
                  <p className="font-outfit text-2xl font-bold text-black">
                    {applications.filter(a => ['scored', 'submitted', 'pending', 'reviewing'].includes(a.status)).length}
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl border border-gray-200 p-6"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#00D47E]/10 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-[#00D47E]" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Approved</p>
                  <p className="font-outfit text-2xl font-bold text-black">
                    {applications.filter(a => ['approved', 'conditionally_approved', 'completed'].includes(a.status)).length}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Applications List */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h2 className="font-outfit text-xl font-bold text-black">
                Your Applications
              </h2>
            </div>

            {loading ? (
              <div className="p-12 text-center">
                <div className="animate-pulse text-gray-500">Loading applications...</div>
              </div>
            ) : applications.length === 0 ? (
              <div className="p-12 text-center">
                <Car className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="font-outfit text-lg font-semibold text-gray-700 mb-2">
                  No Applications Yet
                </h3>
                <p className="text-gray-500 mb-6">
                  Start by calculating your finance options
                </p>
                <Button
                  onClick={() => navigate('/calculator')}
                  className="bg-black hover:bg-gray-900 rounded-full"
                >
                  Calculate Now
                </Button>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {applications.map((app, idx) => (
                  <motion.div
                    key={app.application_id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: idx * 0.05 }}
                    data-testid={`application-${app.application_id}`}
                    className="p-6 hover:bg-white transition-colors"
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-[#00D47E]/10 rounded-lg flex items-center justify-center shrink-0">
                          <span className="font-outfit font-bold text-lg text-black">
                            {(app.lender_name || 'L').charAt(0)}
                          </span>
                        </div>
                        <div>
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="font-outfit font-semibold text-black">
                              {app.lender_name || 'Pending Lender Match'}
                            </h3>
                            {getStatusBadge(app.status)}
                          </div>
                          <p className="text-sm text-gray-600">
                            {app.vehicle_year || app.vehicle_make || app.vehicle_model
                              ? `${app.vehicle_year || ''} ${app.vehicle_make || ''} ${app.vehicle_model || ''}`.trim()
                              : 'Vehicle TBD'}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            Applied {formatDate(app.created_at)}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-6 md:text-right">
                        <div>
                          <p className="text-xs text-gray-500">Loan Amount</p>
                          <p className="font-outfit font-semibold text-black">
                            {formatCurrency(app.loan_amount)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Weekly</p>
                          <p className="font-outfit font-semibold text-[#00D47E]">
                            {app.weekly_payment ? formatCurrency(app.weekly_payment) : '—'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Rate</p>
                          <p className="font-outfit font-semibold text-black">
                            {app.estimated_rate != null ? `${app.estimated_rate}%` : '—'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {/* Help Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-8 bg-[#00D47E]/5 border border-gray-400 rounded-xl p-6"
          >
            <h3 className="font-outfit text-lg font-semibold text-gray-900 mb-2">
              Need Help?
            </h3>
            <p className="text-gray-700 mb-4">
              Our team is here to help you through the finance process.
              {CONTACT_PHONE ? ' Call us or send an email.' : ' Send us an email and we will come back to you.'}
            </p>
            <div className="flex gap-4">
              {/* Rendered only when a real number is configured — see config/legal.js. */}
              {CONTACT_PHONE ? (
                <Button variant="outline" className="border-gray-300 text-gray-700">
                  {CONTACT_PHONE}
                </Button>
              ) : null}
              <Button variant="outline" className="border-gray-300 text-gray-700">
                {CONTACT_EMAIL}
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Dashboard;
